import React, { useState } from 'react';
import { useStore } from '../../store/useStore';
import { StatusPill } from '../shopper/Browse';
import { Search, Filter, AlertCircle, X, Package, Clock, TrendingUp, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
type FilterType = 'all' | 'flagged' | 'in-stock' | 'out-of-stock' | 'stashed' | 'earning' | 'not-verified';
export function Menu() {
  const {
    products,
    toggleProductStock,
    updateProductPrice,
    updateProductStockCount
  } = useStore();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const selectedProduct = products.find(p => p.id === selectedProductId);
  const [filter, setFilter] = useState<FilterType>('all');
  const displayedProducts = products.filter(p => {
    switch (filter) {
      case 'flagged':
        return p.status === 'Not verified' || p.status === 'At risk';
      case 'in-stock':
        return p.inStock;
      case 'out-of-stock':
        return !p.inStock;
      case 'stashed':
        return p.status === 'Stashed';
      case 'earning':
        return p.status === 'Earning';
      case 'not-verified':
        return p.status === 'Not verified';
      default:
        return true;
    }
  });
  return <div className="max-w-5xl mx-auto space-y-6" data-id="element-26">
      <div className="flex justify-between items-end" data-id="element-27">
        <div data-id="element-28">
          <h1 className="text-3xl font-bold text-[#252935] mb-2" data-id="element-29">
            Menu management
          </h1>
          <p className="text-[#60646F]" data-id="element-30">
            Manage your inventory and fix flagged items.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[rgba(37,41,53,0.12)] overflow-hidden flex flex-col h-[600px]" data-id="element-31">
        <div className="p-4 border-b border-[rgba(37,41,53,0.12)] flex items-center justify-between bg-gray-50" data-id="element-32">
          <div className="relative w-64" data-id="element-33">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#60646F]" data-id="element-34" />
            <input type="text" placeholder="Search menu..." className="w-full bg-white border border-[rgba(37,41,53,0.12)] rounded-full py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#00CDBE]" data-id="element-35" />
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" data-id="element-36">
            <button onClick={() => setFilter('all')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border whitespace-nowrap ${filter === 'all' ? 'bg-[#252935] text-white border-[#252935]' : 'bg-white text-[#252935] border-[rgba(37,41,53,0.12)] hover:bg-gray-50'}`} data-id="element-37">
              All items
            </button>
            <button onClick={() => setFilter('flagged')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border flex items-center gap-1.5 whitespace-nowrap ${filter === 'flagged' ? 'bg-[#C98A2E] text-white border-[#C98A2E]' : 'bg-white text-[#252935] border-[rgba(37,41,53,0.12)] hover:bg-gray-50'}`} data-id="element-38">
              <AlertCircle className="w-4 h-4" data-id="element-39" />
              Needs attention
            </button>
            <div className="w-px h-6 bg-[rgba(37,41,53,0.12)] self-center mx-1" data-id="element-40" />
            <button onClick={() => setFilter('in-stock')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border whitespace-nowrap ${filter === 'in-stock' ? 'bg-[#252935] text-white border-[#252935]' : 'bg-white text-[#252935] border-[rgba(37,41,53,0.12)] hover:bg-gray-50'}`} data-id="element-41">
              In stock
            </button>
            <button onClick={() => setFilter('out-of-stock')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border whitespace-nowrap ${filter === 'out-of-stock' ? 'bg-[#252935] text-white border-[#252935]' : 'bg-white text-[#252935] border-[rgba(37,41,53,0.12)] hover:bg-gray-50'}`} data-id="element-42">
              Out of stock
            </button>
            <div className="w-px h-6 bg-[rgba(37,41,53,0.12)] self-center mx-1" data-id="element-43" />
            <button onClick={() => setFilter('stashed')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border whitespace-nowrap ${filter === 'stashed' ? 'bg-[#007E7A] text-white border-[#007E7A]' : 'bg-white text-[#252935] border-[rgba(37,41,53,0.12)] hover:bg-gray-50'}`} data-id="element-44">
              Stashed
            </button>
            <button onClick={() => setFilter('earning')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border whitespace-nowrap ${filter === 'earning' ? 'bg-[#60646F] text-white border-[#60646F]' : 'bg-white text-[#252935] border-[rgba(37,41,53,0.12)] hover:bg-gray-50'}`} data-id="element-45">
              Earning
            </button>
            <button onClick={() => setFilter('not-verified')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border whitespace-nowrap ${filter === 'not-verified' ? 'bg-[#C98A2E] text-white border-[#C98A2E]' : 'bg-white text-[#252935] border-[rgba(37,41,53,0.12)] hover:bg-gray-50'}`} data-id="element-46">
              Not verified
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-47">
          <table className="w-full text-left border-collapse" data-id="element-48">
            <thead className="bg-white sticky top-0 z-10 shadow-sm" data-id="element-49">
              <tr data-id="element-50">
                <th className="p-4 text-sm font-bold text-[#60646F] border-b border-[rgba(37,41,53,0.12)]" data-id="element-51">
                  Product
                </th>
                <th className="p-4 text-sm font-bold text-[#60646F] border-b border-[rgba(37,41,53,0.12)]" data-id="element-52">
                  Category
                </th>
                <th className="p-4 text-sm font-bold text-[#60646F] border-b border-[rgba(37,41,53,0.12)]" data-id="element-53">
                  Price
                </th>
                <th className="p-4 text-sm font-bold text-[#60646F] border-b border-[rgba(37,41,53,0.12)]" data-id="element-54">
                  In Stock
                </th>
                <th className="p-4 text-sm font-bold text-[#60646F] border-b border-[rgba(37,41,53,0.12)]" data-id="element-55">
                  Stash Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(37,41,53,0.12)]" data-id="element-56">
              {displayedProducts.map(product => <tr key={product.id} onClick={() => setSelectedProductId(product.id)} className="hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-57">
                  <td className="p-4" data-id="element-58">
                    <div className="flex items-center gap-3" data-id="element-59">
                      <img src={product.image} alt={product.name} className="w-10 h-10 rounded-lg object-cover" data-id="element-60" />
                      <div data-id="element-61">
                        <div className="font-bold text-[#252935] flex items-center gap-2" data-id="element-62">
                          {product.name}
                          {product.failedPickups > 0 && <AlertCircle className="w-4 h-4 text-[#C98A2E]" data-id="element-63" />}
                        </div>
                        <div className="text-xs text-[#60646F]" data-id="element-64">
                          Last confirmed: {product.lastUpdated}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-[#252935]" data-id="element-65">
                    {product.category}
                  </td>
                  <td className="p-4" data-id="element-66">
                    <div className="relative w-20" data-id="element-67">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#60646F]" data-id="element-68">
                        $
                      </span>
                      <input type="number" value={product.price} onClick={e => e.stopPropagation()} onChange={e => updateProductPrice(product.id, Number(e.target.value))} className="w-full border border-[rgba(37,41,53,0.12)] rounded-md py-1 pl-5 pr-2 text-sm focus:outline-none focus:border-[#00CDBE]" data-id="element-69" />
                    </div>
                  </td>
                  <td className="p-4" data-id="element-70">
                    <button onClick={e => {
                  e.stopPropagation();
                  toggleProductStock(product.id);
                }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${product.inStock ? 'bg-[#00CDBE]' : 'bg-gray-300'}`} data-id="element-71">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${product.inStock ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-72" />
                    </button>
                  </td>
                  <td className="p-4" data-id="element-73">
                    <StatusPill status={product.status} data-id="element-74" />
                  </td>
                </tr>)}
            </tbody>
          </table>
          {displayedProducts.length === 0 && <div className="p-12 text-center text-[#60646F]" data-id="element-75">
              <Filter className="w-8 h-8 mx-auto mb-3 opacity-40" data-id="element-76" />
              <p className="font-bold text-[#252935] mb-1" data-id="element-77">
                No items match this filter
              </p>
              <p className="text-sm" data-id="element-78">
                Try a different filter to see more of your menu.
              </p>
            </div>}
        </div>
      </div>

      {/* Product Detail Drawer */}
      <AnimatePresence data-id="element-79">
        {selectedProduct && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedProductId(null)} className="fixed inset-0 bg-black/20 z-40" data-id="element-80" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 h-full w-[400px] bg-white shadow-2xl z-50 flex flex-col border-l border-[rgba(37,41,53,0.12)]" data-id="element-81">
              <div className="flex items-center justify-between p-6 border-b border-[rgba(37,41,53,0.12)]" data-id="element-82">
                <h2 className="text-xl font-bold text-[#252935]" data-id="element-83">
                  Inventory details
                </h2>
                <button onClick={() => setSelectedProductId(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#60646F]" data-id="element-84">
                  <X className="w-5 h-5" data-id="element-85" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-86">
                {/* Product Header */}
                <div className="flex gap-4" data-id="element-87">
                  <img src={selectedProduct.image} alt={selectedProduct.name} className="w-20 h-20 rounded-xl object-cover border border-[rgba(37,41,53,0.12)]" data-id="element-88" />
                  <div data-id="element-89">
                    <div className="text-sm font-bold text-[#60646F] uppercase tracking-wider mb-1" data-id="element-90">
                      {selectedProduct.brand}
                    </div>
                    <h3 className="text-lg font-bold text-[#252935] leading-tight mb-2" data-id="element-91">
                      {selectedProduct.name}
                    </h3>
                    <StatusPill status={selectedProduct.status} data-id="element-92" />
                  </div>
                </div>

                {/* Stock Management Hero */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-[rgba(37,41,53,0.12)]" data-id="element-93">
                  <div className="flex items-center justify-between mb-4" data-id="element-94">
                    <div className="flex items-center gap-2 text-[#60646F] font-bold" data-id="element-95">
                      <Package className="w-5 h-5" data-id="element-96" />
                      Units in stock
                    </div>
                    {selectedProduct.stockCount === 0 ? <span className="flex items-center gap-1 text-sm font-bold text-[#D93A4B] bg-[#FFF0F2] px-2.5 py-1 rounded-md" data-id="element-97">
                        <AlertTriangle className="w-4 h-4" data-id="element-98" />
                        Out of stock
                      </span> : selectedProduct.stockCount <= selectedProduct.reorderPoint ? <span className="flex items-center gap-1 text-sm font-bold text-[#C98A2E] bg-[#FFF8ED] px-2.5 py-1 rounded-md" data-id="element-99">
                        <AlertTriangle className="w-4 h-4" data-id="element-100" />
                        Low stock
                      </span> : null}
                  </div>

                  <div className="flex items-end gap-4" data-id="element-101">
                    <input type="number" value={selectedProduct.stockCount} onChange={e => updateProductStockCount(selectedProduct.id, parseInt(e.target.value) || 0)} className={clsx('text-5xl font-extrabold bg-white border rounded-xl w-32 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#00CDBE]', selectedProduct.stockCount === 0 ? 'text-[#D93A4B] border-[#D93A4B]' : selectedProduct.stockCount <= selectedProduct.reorderPoint ? 'text-[#C98A2E] border-[#C98A2E]' : 'text-[#252935] border-[rgba(37,41,53,0.12)]')} data-id="element-102" />
                    <div className="mb-3 text-[#60646F]" data-id="element-103">
                      Reorder point: {selectedProduct.reorderPoint}
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4" data-id="element-104">
                  <div className="bg-white border border-[rgba(37,41,53,0.12)] rounded-xl p-4" data-id="element-105">
                    <div className="text-sm text-[#60646F] mb-1" data-id="element-106">
                      Package size
                    </div>
                    <div className="font-bold text-[#252935]" data-id="element-107">
                      {selectedProduct.size}
                    </div>
                  </div>
                  <div className="bg-white border border-[rgba(37,41,53,0.12)] rounded-xl p-4" data-id="element-108">
                    <div className="text-sm text-[#60646F] mb-1" data-id="element-109">
                      Last updated
                    </div>
                    <div className="font-bold text-[#252935] flex items-center gap-1.5" data-id="element-110">
                      <Clock className="w-4 h-4 text-[#007E7A]" data-id="element-111" />
                      {selectedProduct.lastUpdated}
                    </div>
                  </div>
                </div>

                {/* Recent Sales */}
                <div data-id="element-112">
                  <h4 className="font-bold text-[#252935] flex items-center gap-2 mb-4" data-id="element-113">
                    <TrendingUp className="w-5 h-5 text-[#60646F]" data-id="element-114" />
                    Recent sales
                  </h4>
                  {selectedProduct.recentSales && selectedProduct.recentSales.length > 0 ? <div className="space-y-3" data-id="element-115">
                      {selectedProduct.recentSales.map((sale, i) => <div key={i} className="flex items-center justify-between py-3 border-b border-[rgba(37,41,53,0.12)] last:border-0" data-id="element-116">
                          <div data-id="element-117">
                            <div className="font-bold text-[#252935]" data-id="element-118">
                              {sale.qty} unit{sale.qty > 1 ? 's' : ''}
                            </div>
                            <div className="text-sm text-[#60646F]" data-id="element-119">
                              {sale.time}
                            </div>
                          </div>
                          <div className="font-bold text-[#007E7A]" data-id="element-120">
                            ${(sale.price * sale.qty).toFixed(2)}
                          </div>
                        </div>)}
                    </div> : <div className="text-center py-8 text-[#60646F] bg-gray-50 rounded-xl border border-[rgba(37,41,53,0.12)] border-dashed" data-id="element-121">
                      No recent sales data
                    </div>}
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}